import { ReebeloStoreT } from '@lambda/reebelo';
import {
  BadgeCheckIcon,
  Check,
  PackageIcon,
  ShieldCheckIcon,
} from 'lucide-react';

/**
 * Mini condition descriptions (the Screen / Body / Battery preview line under the
 * condition selector) for every non–"tech device" PDP.
 *
 * ⚠️  AUTO-GENERATED from the "Mini Condition Description Copy" sheet (RT-16938).
 *     The copy here is catalog content living in code (see LEGACY.md → "Hard-coded
 *     catalog logic in the storefront"). It is an interim, storefront-only solution;
 *     the source of truth for this copy should move to MsCatalog2 alongside the full
 *     condition descriptions served by `/api/conditions/[categoryPk]`. Do not extend
 *     this map by hand — regenerate it from the sheet, or migrate it to MsCatalog2.
 *
 * Keyed by department (the product's top-level category, resolved from the PDP
 * breadcrumb) → condition (the selected condition variant value) → the icon rows.
 * `overrides` are keyed by the leaf category name and take precedence, per condition,
 * over the department `default`.
 *
 * Tech devices (Laptops / Smartwatches / Tablets / Smartphones) are intentionally
 * NOT here — they keep the original Screen/Body/Battery map in the component, so
 * existing electronics copy is unchanged.
 */
export type MiniConditionPart = {
  title: string;
  icon: typeof Check;
  text: string;
};

type ConditionCopy = Record<string, MiniConditionPart[]>;

type DepartmentCopy = {
  default: ConditionCopy;
  overrides?: Record<string, ConditionCopy>;
};

// MsCatalog2 currently returns localized condition values for the EU stores
// (for example, "Comme neuf"), despite `value` normally being the canonical
// catalog key. Keep the copy map keyed by its English spreadsheet values and
// normalize the known EU labels at the boundary. Remove these aliases once the
// catalog resumes returning canonical values with localized `displayValue`s.
const CANONICAL_CONDITION_BY_LOCALIZED_VALUE: Record<string, string> = {
  bon: 'Good',
  'très bon': 'Very Good',
  'comme neuf': 'Like New',
  neuf: 'Brand New',
  'boîte ouverte': 'Open Box',
  gut: 'Good',
  'sehr gut': 'Very Good',
  'wie neu': 'Like New',
  neu: 'Brand New',
  'geöffnete verpackung': 'Open Box',
  bueno: 'Good',
  'muy bueno': 'Very Good',
  'como nuevo': 'Like New',
  nuevo: 'Brand New',
  'totalmente nuevo': 'Brand New',
  'caja abierta': 'Open Box',
  buono: 'Good',
  'molto buono': 'Very Good',
  'come nuovo': 'Like New',
  nuovo: 'Brand New',
  'nuovo di zecca': 'Brand New',
  'confezione aperta': 'Open Box',
};

export const getCanonicalMiniCondition = (
  condition: string,
  store: ReebeloStoreT,
): string => {
  if (
    (store === 'reebelo-de' || store === 'reebelo-at') &&
    condition === 'Premium'
  ) {
    return 'Like New';
  }

  return (
    CANONICAL_CONDITION_BY_LOCALIZED_VALUE[condition.trim().toLowerCase()] ??
    condition
  );
};

export const MINI_CONDITION_COPY: Record<string, DepartmentCopy> = {
  'Baby Products & Toys': {
    default: {
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
  Electronics: {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Brand New': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, in original packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
  'Fashion & Jewelry': {
    default: {
      'Very Good': [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: 'Minor signs of wear (light scuffs or fading). Fully inspected for quality and durability',
        },
      ],
      'Like New': [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: 'Little to no visible wear. Fully inspected for quality and durability.',
        },
      ],
      'New with Tags': [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: 'Unworn, with original tags attached.',
        },
      ],
      'New without Tags': [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: 'Unworn, tags removed or not included.',
        },
      ],
    },
  },
  Gaming: {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
    overrides: {
      'Gaming Accessories': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
      },
      'Gaming Consoles': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
      },
      'PC Gaming': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
      },
      'Video Games': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
      },
    },
  },
  'Health & Beauty': {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
    overrides: {
      'Biometric Monitors': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      Cosmetics: {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      Fragrance: {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Hair Care': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Health & Beauty Bundle': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Health & Body': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Health Care': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Personal Care': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
      'Skin Care': {
        Good: [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
          },
        ],
        'Brand New': [
          {
            title: '',
            icon: ShieldCheckIcon,
            text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
          },
        ],
      },
    },
  },
  'Hobbies & Creative Arts': {
    default: {
      Good: [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: "May have visible signs of wear (scratches, marks, or other blemishes) at arm's length. Fully tested and guaranteed to work.",
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Brand New': [
        {
          title: '',
          icon: ShieldCheckIcon,
          text: 'Unused, often returned in original packaging. Fully tested and guaranteed to work.',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
  'Home & Kitchen': {
    default: {
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
    overrides: {
      'Heating, Cooling & Air Quality': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Home Appliances': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Home Decor Products': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Home Office Furniture': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Household Supplies': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      Housekeeping: {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      Ironing: {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Kitchen Accessories': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Kitchen Appliances': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Kitchen Tools & Utensils': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Office Supplies': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Shoe Care & Tools': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Storage & Organisation': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      Tableware: {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
      'Vacuum Cleaners & Floor Care': {
        'Very Good': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'Minor signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
        'Like New': [
          {
            title: 'Body',
            icon: PackageIcon,
            text: 'No visible signs of wear, cleaned and sanitized',
          },
          {
            title: 'Performance',
            icon: BadgeCheckIcon,
            text: 'Experty tested and guaranteed to work',
          },
        ],
      },
    },
  },
  Others: {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
  'Sports & Fitness': {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
  'Tools & Home Improvement': {
    default: {
      Good: [
        { title: 'Body', icon: PackageIcon, text: 'Visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Very Good': [
        { title: 'Body', icon: PackageIcon, text: 'Minor signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Like New': [
        { title: 'Body', icon: PackageIcon, text: 'No visible signs of wear' },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
      'Open Box': [
        {
          title: 'Body',
          icon: PackageIcon,
          text: 'Unused, open-box packaging',
        },
        {
          title: 'Performance',
          icon: BadgeCheckIcon,
          text: 'Experty tested and guaranteed to work',
        },
      ],
    },
  },
};

/**
 * Resolve the department key from the PDP breadcrumb (root → leaf). Returns the
 * first breadcrumb entry that has copy defined, or null when none matches.
 */
export const getMiniConditionDepartment = (
  breadcrumb: { name: string }[] | null | undefined,
): string | null =>
  breadcrumb?.find((b) => MINI_CONDITION_COPY[b.name])?.name ?? null;

/**
 * Parts for a given department + leaf category + selected condition. Leaf overrides
 * win per-condition, otherwise the department default is used. Returns [] when there
 * is no copy for that combination (the caller then renders nothing).
 */
export const getMiniConditionParts = (
  department: string | null,
  categoryName: string,
  condition: string,
  store: ReebeloStoreT,
): MiniConditionPart[] => {
  if (!department) {
    return [];
  }
  const dept = MINI_CONDITION_COPY[department];

  if (!dept) {
    return [];
  }

  const canonicalCondition = getCanonicalMiniCondition(condition, store);

  return (
    dept.overrides?.[categoryName]?.[canonicalCondition] ??
    dept.default[canonicalCondition] ??
    []
  );
};
